import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SearchIcon from './SearchIcon';
import Icon from './Icon';

/**
 * Full-width search overlay that drops down from the header.
 * Triggered by the search button in Header.
 */
const SearchOverlay = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 150);
    }
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const sections = [
    { label: 'Notizie', path: '/notizie', icon: 'Newspaper' },
    { label: 'Eventi', path: '/eventi', icon: 'Calendar' },
    { label: 'Persone', path: '/persone', icon: 'Users' },
    { label: 'Circoli', path: '/circoli', icon: 'MapPin' },
    { label: 'Federazioni', path: '/federazioni', icon: 'Map' },
    { label: 'Video', path: '/video', icon: 'Video' },
    { label: 'Podcast', path: '/podcast', icon: 'Mic' },
    { label: 'Documenti', path: '/documenti', icon: 'FileText' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`search-overlay-backdrop${isOpen ? ' is-open' : ''}`}
        onClick={onClose}
      />

      {/* Overlay panel */}
      <div className={`search-overlay${isOpen ? ' is-open' : ''}`} role="dialog" aria-label="Cerca nel sito">
        <div className="container">
          <div className="search-overlay__bar">
            <SearchIcon className="search-overlay__icon" />
            <input
              ref={inputRef}
              type="text"
              className="search-overlay__input"
              placeholder="Cerca notizie, eventi, persone, circoli..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Cerca nel sito"
            />
            <button className="search-overlay__close" onClick={onClose} aria-label="Chiudi ricerca">
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Quick links when empty */}
          {query.length === 0 && (
            <div className="search-overlay__quick">
              <span className="search-overlay__quick-label">Esplora</span>
              <div className="search-overlay__quick-grid">
                {sections.map((s) => (
                  <Link to={s.path} className="search-overlay__quick-link" key={s.path} onClick={onClose}>
                    <Icon name={s.icon} size={18} />
                    <span>{s.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Results placeholder */}
          {query.length > 0 && (
            <div className="search-overlay__results">
              <p className="text-secondary">Nessun risultato per "<strong>{query}</strong>"</p>
              <p className="text-meta mt-2">Prova con un termine diverso o esplora le sezioni.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchOverlay;
